import { useState, useEffect, useCallback, useMemo } from "react";
import { api } from "../App";
import { getApiPrefix } from "../utils/apiHelper";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { SearchableSelect } from "../components/ui/searchable-select";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import DataTable from "../components/ui/data-table";
import { handleApiError, showSuccess } from "../utils/errorHandler";
import { PageLoader } from "../components/ui/page-loader";
import { 
  Loader2, Pencil, Trash2, TrendingDown, Calculator, 
  ChevronDown, ChevronRight, Euro, Calendar, Building,
  BarChart3, PieChart as PieChartIcon
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as ReTooltip,
  ResponsiveContainer, Cell, Legend
} from "recharts";
import { useTranslation } from "../contexts/I18nContext";

// Formatta valuta
const formatCurrency = (value) => {
  if (value === null || value === undefined) return "-";
  return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(value);
};

// Calcola il piano di ammortamento per un bene
function calcolaPianoAmmortamento(valoreBene, quotaAnnuale, dataInizio) {
  const piano = [];
  let residuo = valoreBene;
  
  // Parse date in multiple formats
  let annoInizio;
  if (typeof dataInizio === 'string' && dataInizio.includes('-')) {
    const parts = dataInizio.split('-');
    if (parts[0].length === 4) {
      annoInizio = parseInt(parts[0]);
    } else {
      annoInizio = parseInt(parts[2]);
    }
  } else {
    annoInizio = new Date(dataInizio).getFullYear();
  }
  if (isNaN(annoInizio)) return piano;
  
  let anno = annoInizio;
  let totaleAmmortizzato = 0;
  
  while (residuo > 0.01) { // Tolleranza per arrotondamenti
    let quotaAnno;
    
    if (anno === annoInizio) {
      // Primo anno: 50% della quota annuale
      quotaAnno = (valoreBene * quotaAnnuale / 100) / 2;
    } else {
      // Anni successivi: quota piena
      quotaAnno = valoreBene * quotaAnnuale / 100;
    }
    
    // Non superare il residuo
    if (quotaAnno > residuo) {
      quotaAnno = residuo;
    }
    
    residuo -= quotaAnno;
    totaleAmmortizzato += quotaAnno;
    
    piano.push({
      anno,
      quotaAnno: Math.round(quotaAnno * 100) / 100,
      totaleAmmortizzato: Math.round(totaleAmmortizzato * 100) / 100,
      residuo: Math.round(residuo * 100) / 100,
      isPrimoAnno: anno === annoInizio
    });
    
    anno++;
    
    // Sicurezza: max 50 anni
    if (piano.length > 50) break;
  }
  
  return piano;
}

// Calcola il residuo ammortizzabile a una specifica data di riferimento.
// `riferimento`: "oggi" | "fineAnnoCorr" | "fineAnnoPrec"
// Restituisce un numero (0 se ammortamento completato, valoreBene se prima dell'inizio).
function calcolaResiduoAdData(piano, valoreBene, riferimento) {
  const now = new Date();
  const currentYear = now.getFullYear();
  if (!piano || piano.length === 0) {
    // Nessun piano: presumi non ancora iniziato => valore pieno
    return valoreBene;
  }
  const primoAnno = piano[0].anno;
  if (riferimento === "fineAnnoPrec") {
    const anno = currentYear - 1;
    const row = piano.find(p => p.anno === anno);
    if (row) return row.residuo;
    if (anno < primoAnno) return valoreBene;
    return 0; // ammortamento completato prima dell'anno precedente
  }
  if (riferimento === "fineAnnoCorr") {
    const row = piano.find(p => p.anno === currentYear);
    if (row) return row.residuo;
    if (currentYear < primoAnno) return valoreBene;
    return 0;
  }
  // Default: "oggi" — residuo prorata sulla frazione di anno trascorsa
  if (currentYear < primoAnno) return valoreBene;
  const rigaAnnoCorr = piano.find(p => p.anno === currentYear);
  if (!rigaAnnoCorr) {
    // Ammortamento completato in anni precedenti
    return 0;
  }
  // Inizio anno = residuo a fine anno precedente (oppure valore bene se è il primo anno)
  const rigaAnnoPrec = piano.find(p => p.anno === currentYear - 1);
  const residuoInizioAnno = rigaAnnoPrec ? rigaAnnoPrec.residuo : valoreBene;
  // Frazione di anno trascorsa al giorno odierno
  const inizioAnno = new Date(currentYear, 0, 1);
  const fineAnno = new Date(currentYear + 1, 0, 1);
  const frazione = Math.max(0, Math.min(1, (now - inizioAnno) / (fineAnno - inizioAnno)));
  const consumatoOggi = rigaAnnoCorr.quotaAnno * frazione;
  const residuo = residuoInizioAnno - consumatoOggi;
  return Math.max(0, Math.round(residuo * 100) / 100);
}

export default function PianoAmmortamentiPage() {
  const { t } = useTranslation();
  const [ammortamenti, setAmmortamenti] = useState([]);
  const [categorie, setCategorie] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedRows, setExpandedRows] = useState({});
  const [editDialog, setEditDialog] = useState({ open: false, item: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, item: null });
  // Selettore "Residuo di riferimento" per i grafici della dashboard.
  // Valori: "oggi" | "fineAnnoCorr" | "fineAnnoPrec"
  const [residuoMode, setResiduoMode] = useState("oggi");
  
  const [editForm, setEditForm] = useState({
    descrizione_bene: "",
    categoria_id: "",
    note: ""
  });

  const fetchData = useCallback(async () => {
    try {
      const prefix = getApiPrefix();
      const [ammRes, catRes] = await Promise.all([
        api.get(`${prefix}/ammortamenti`),
        api.get(`${prefix}/categorie-immobilizzazioni`)
      ]);
      setAmmortamenti(ammRes.data);
      setCategorie(catRes.data);
    } catch (err) {
      handleApiError(err, t("errors.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Calcola statistiche
  const stats = useMemo(() => {
    const currentYear = new Date().getFullYear();
    let totaleBeni = 0;
    let totaleAmmortizzatoAnnoCorrente = 0;
    let totaleResiduoGlobale = 0;
    
    ammortamenti.forEach(amm => {
      const piano = calcolaPianoAmmortamento(amm.valore_bene, amm.quota_annuale_percentuale, amm.data_inizio);
      totaleBeni += amm.valore_bene;
      
      const quotaAnnoCorrente = piano.find(p => p.anno === currentYear);
      if (quotaAnnoCorrente) {
        totaleAmmortizzatoAnnoCorrente += quotaAnnoCorrente.quotaAnno;
      }
      
      // Residuo è il valore dell'anno corrente (non dell'ultimo anno del piano)
      const annoCorrente = piano.find(p => p.anno === currentYear);
      if (annoCorrente) {
        totaleResiduoGlobale += annoCorrente.residuo;
      } else {
        // Se l'anno corrente è prima dell'inizio piano, usa valore bene intero
        // Se è dopo la fine del piano, residuo = 0
        const primoAnno = piano.length > 0 ? piano[0].anno : currentYear + 1;
        if (currentYear < primoAnno) {
          totaleResiduoGlobale += amm.valore_bene;
        }
        // else: ammortamento completato, residuo = 0
      }
    });
    
    return {
      totaleBeni,
      totaleAmmortizzatoAnnoCorrente,
      totaleResiduoGlobale,
      numeroAmmortamenti: ammortamenti.length
    };
  }, [ammortamenti]);

  // Dashboard: residuo per categoria e timeline ammortamenti
  // Il residuo per categoria si aggiorna in base al `residuoMode` (oggi / fine anno corr / fine anno prec).
  const dashboardData = useMemo(() => {
    const currentYear = new Date().getFullYear();
    
    // Residuo per categoria
    const perCategoria = {};
    // Timeline: per ogni anno, importo in ammortamento
    const anniSet = new Set();
    
    ammortamenti.forEach(amm => {
      const piano = calcolaPianoAmmortamento(amm.valore_bene, amm.quota_annuale_percentuale, amm.data_inizio);
      const cat = categorie.find(c => c.id === amm.categoria_id);
      const catNome = cat?.nome || "Senza Categoria";
      const catId = amm.categoria_id || "__none__";
      
      if (!perCategoria[catId]) {
        perCategoria[catId] = { nome: catNome, valoreBene: 0, ammortizzato: 0, residuo: 0, count: 0 };
      }
      perCategoria[catId].valoreBene += amm.valore_bene;
      perCategoria[catId].count += 1;
      
      // Calcola residuo alla data di riferimento selezionata
      const residuo = calcolaResiduoAdData(piano, amm.valore_bene, residuoMode);
      perCategoria[catId].residuo += residuo;
      perCategoria[catId].ammortizzato += Math.max(0, amm.valore_bene - residuo);
      
      // Timeline
      piano.forEach(p => anniSet.add(p.anno));
    });
    
    // Build timeline data
    const anniOrdinati = Array.from(anniSet).sort();
    const timelineData = anniOrdinati.map(anno => {
      const entry = { anno: String(anno) };
      let totQuota = 0;
      ammortamenti.forEach(amm => {
        const piano = calcolaPianoAmmortamento(amm.valore_bene, amm.quota_annuale_percentuale, amm.data_inizio);
        const p = piano.find(pp => pp.anno === anno);
        if (p) totQuota += p.quotaAnno;
      });
      entry.quota = Math.round(totQuota * 100) / 100;
      entry.isCurrent = anno === currentYear;
      return entry;
    });
    
    const categorieArr = Object.values(perCategoria).sort((a, b) => b.residuo - a.residuo);
    
    return { categorieArr, timelineData };
  }, [ammortamenti, categorie, residuoMode]);

  const toggleExpand = (id) => {
    setExpandedRows(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const openEditDialog = (item) => {
    setEditDialog({ open: true, item });
    setEditForm({
      descrizione_bene: item.descrizione_bene || "",
      categoria_id: item.categoria_id || "",
      note: item.note || ""
    });
  };

  const handleEditSubmit = async () => {
    if (!editForm.descrizione_bene || !editForm.categoria_id) {
      handleApiError({ message: t("errors.requiredCategory") });
      return;
    }
    
    try {
      const params = new URLSearchParams({
        descrizione_bene: editForm.descrizione_bene,
        categoria_id: editForm.categoria_id
      });
      if (editForm.note) params.append("note", editForm.note);
      
      await api.put(`${getApiPrefix()}/ammortamenti/${editDialog.item.id}?${params.toString()}`);
      showSuccess(t("success.amortizationUpdated"));
      setEditDialog({ open: false, item: null });
      fetchData();
    } catch (err) {
      handleApiError(err, t("errors.updateFailed"));
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`${getApiPrefix()}/ammortamenti/${deleteDialog.item.id}`);
      showSuccess(t("success.amortizationDeleted"));
      setDeleteDialog({ open: false, item: null });
      fetchData();
    } catch (err) {
      handleApiError(err, t("errors.deleteFailed"));
    }
  };

  const handleBulkDelete = async (ids) => {
    let deleted = 0;
    const prefix = getApiPrefix();
    for (const id of ids) {
      try {
        await api.delete(`${prefix}/ammortamenti/${id}`);
        deleted++;
      } catch {
        // Ignore individual delete errors; count only successes
      }
    }
    showSuccess(`${deleted} ammortamenti eliminati`);
    fetchData();
  };

  // Colonne per DataTable
  const columns = [
    {
      key: 'expand',
      label: '',
      sortable: false,
      filterable: false,
      exportable: false,
      render: (_, row) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => { e.stopPropagation(); toggleExpand(row.id); }}
        >
          {expandedRows[row.id] ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </Button>
      )
    },
    {
      key: 'descrizione_bene',
      label: 'Bene',
      render: (value) => <span className="font-medium">{value}</span>
    },
    {
      key: 'categoria_nome',
      label: t('common.category'),
      render: (value, row) => (
        <Badge variant="outline">
          {value} ({row.quota_annuale_percentuale}%)
        </Badge>
      )
    },
    {
      key: 'valore_bene',
      label: 'Valore Bene',
      render: (value) => <span className="font-mono">{formatCurrency(value)}</span>
    },
    {
      key: 'data_inizio',
      label: 'Data Inizio',
      render: (value) => value || "-"
    },
    {
      key: 'fattura_info',
      label: 'Fattura',
      getValue: (row) => row.fattura_numero,
      render: (value, row) => (
        <div className="text-sm">
          <span className="font-medium">{value || "-"}</span>
          {row.fornitore && <div className="text-xs text-muted-foreground truncate max-w-[120px]">{row.fornitore}</div>}
        </div>
      )
    },
    {
      key: 'residuo_calcolato',
      label: 'Residuo ad oggi',
      getValue: (row) => {
        const piano = calcolaPianoAmmortamento(row.valore_bene, row.quota_annuale_percentuale, row.data_inizio);
        return calcolaResiduoAdData(piano, row.valore_bene, "oggi");
      },
      render: (value) => (
        <span className={`font-mono ${value > 0 ? 'text-amber-600' : 'text-green-600'}`}>
          {formatCurrency(value)}
        </span>
      )
    },
    {
      key: 'residuo_fine_anno_prec',
      label: `Residuo 31/12/${new Date().getFullYear() - 1}`,
      getValue: (row) => {
        const piano = calcolaPianoAmmortamento(row.valore_bene, row.quota_annuale_percentuale, row.data_inizio);
        return calcolaResiduoAdData(piano, row.valore_bene, "fineAnnoPrec");
      },
      render: (value) => (
        <span className={`font-mono ${value > 0 ? 'text-amber-700' : 'text-green-600'}`}>
          {formatCurrency(value)}
        </span>
      )
    },
    {
      key: 'residuo_fine_anno_corr',
      label: `Residuo 31/12/${new Date().getFullYear()}`,
      getValue: (row) => {
        const piano = calcolaPianoAmmortamento(row.valore_bene, row.quota_annuale_percentuale, row.data_inizio);
        return calcolaResiduoAdData(piano, row.valore_bene, "fineAnnoCorr");
      },
      render: (value) => (
        <span className={`font-mono ${value > 0 ? 'text-amber-700' : 'text-green-600'}`}>
          {formatCurrency(value)}
        </span>
      )
    },
    {
      key: 'actions',
      label: t('common.actions'),
      align: 'right',
      sortable: false,
      filterable: false,
      exportable: false,
      render: (_, row) => (
        <div className="flex justify-end gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => openEditDialog(row)}
            data-testid={`edit-${row.id}`}
          >
            <Pencil className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setDeleteDialog({ open: true, item: row })}
            className="text-destructive"
            data-testid={`delete-${row.id}`}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      )
    }
  ];

  // Render riga espansa con piano ammortamento
  const renderExpandedRow = (row) => {
    if (!expandedRows[row.id]) return null;
    
    const piano = calcolaPianoAmmortamento(row.valore_bene, row.quota_annuale_percentuale, row.data_inizio);
    const currentYear = new Date().getFullYear();
    
    return (
      <tr key={`${row.id}-expanded`}>
        <td colSpan={columns.length} className="p-0">
          <div className="bg-muted/30 p-4 border-t">
            <h4 className="font-medium mb-3 flex items-center gap-2">
              <Calculator className="h-4 w-4" />
              Piano di Ammortamento - {row.descrizione_bene}
            </h4>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-3">{t("common.year")}</th>
                    <th className="text-right py-2 px-3">Quota Anno</th>
                    <th className="text-right py-2 px-3">Tot. Ammortizzato</th>
                    <th className="text-right py-2 px-3">Residuo</th>
                    <th className="text-left py-2 px-3">{t("common.notes")}</th>
                  </tr>
                </thead>
                <tbody>
                  {piano.map((p, idx) => (
                    <tr 
                      key={p.anno ?? idx} 
                      className={`border-b ${p.anno === currentYear ? 'bg-blue-50' : ''}`}
                    >
                      <td className="py-2 px-3">
                        <span className={p.anno === currentYear ? 'font-bold' : ''}>
                          {p.anno}
                        </span>
                        {p.anno === currentYear && (
                          <Badge variant="secondary" className="ml-2 text-xs">{t('common.current')}</Badge>
                        )}
                      </td>
                      <td className="text-right py-2 px-3 font-mono">
                        {formatCurrency(p.quotaAnno)}
                      </td>
                      <td className="text-right py-2 px-3 font-mono text-muted-foreground">
                        {formatCurrency(p.totaleAmmortizzato)}
                      </td>
                      <td className="text-right py-2 px-3 font-mono">
                        <span className={p.residuo > 0 ? 'text-amber-600' : 'text-green-600'}>
                          {formatCurrency(p.residuo)}
                        </span>
                      </td>
                      <td className="py-2 px-3 text-muted-foreground text-xs">
                        {p.isPrimoAnno ? "50% (primo anno)" : ""}
                        {p.residuo === 0 ? "Ammortamento completato" : ""}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </td>
      </tr>
    );
  };

  if (loading) {
    return <PageLoader variant="section" text={t("common.loading")} />;
  }

  return (
    <div className="space-y-6" data-testid="piano-ammortamenti-page">
      <div>
        <h1 className="text-2xl font-bold">{t("amortization.plan")}</h1>
        <p className="text-muted-foreground">Visualizza il piano di ammortamento dei beni aziendali</p>
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Building className="h-8 w-8 text-blue-500" />
              <div>
                <p className="text-xs text-muted-foreground uppercase">Beni in Ammortamento</p>
                <p className="text-2xl font-bold">{stats.numeroAmmortamenti}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Euro className="h-8 w-8 text-green-500" />
              <div>
                <p className="text-xs text-muted-foreground uppercase">Valore Totale Beni</p>
                <p className="text-2xl font-bold">{formatCurrency(stats.totaleBeni)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <TrendingDown className="h-8 w-8 text-purple-500" />
              <div>
                <p className="text-xs text-muted-foreground uppercase">Ammortamento {new Date().getFullYear()}</p>
                <p className="text-2xl font-bold">{formatCurrency(stats.totaleAmmortizzatoAnnoCorrente)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Calendar className="h-8 w-8 text-amber-500" />
              <div>
                <p className="text-xs text-muted-foreground uppercase">Residuo Ammortizzabile</p>
                <p className="text-2xl font-bold text-amber-600">{formatCurrency(stats.totaleResiduoGlobale)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Dashboard: Residuo per Categoria e Timeline Ammortamenti */}
      {ammortamenti.length > 0 && (
        <div className="space-y-3" data-testid="dashboard-ammortamenti">
          {/* Toggle modalità calcolo residuo */}
          <div className="flex flex-wrap items-center justify-between gap-2 px-1">
            <p className="text-sm text-muted-foreground">
              <span className="font-medium text-foreground">Residuo di riferimento:</span>{' '}
              {residuoMode === 'oggi' && `Ad oggi (${new Date().toLocaleDateString('it-IT')})`}
              {residuoMode === 'fineAnnoPrec' && `Al 31/12/${new Date().getFullYear() - 1}`}
              {residuoMode === 'fineAnnoCorr' && `Al 31/12/${new Date().getFullYear()}`}
            </p>
            <div className="inline-flex rounded-md border bg-background p-0.5" role="group" data-testid="residuo-toggle">
              <Button
                variant={residuoMode === 'oggi' ? 'default' : 'ghost'}
                size="sm"
                className="h-7 px-2.5 text-xs"
                onClick={() => setResiduoMode('oggi')}
                data-testid="residuo-mode-oggi"
              >
                Ad oggi
              </Button>
              <Button
                variant={residuoMode === 'fineAnnoPrec' ? 'default' : 'ghost'}
                size="sm"
                className="h-7 px-2.5 text-xs"
                onClick={() => setResiduoMode('fineAnnoPrec')}
                data-testid="residuo-mode-prec"
              >
                31/12/{new Date().getFullYear() - 1}
              </Button>
              <Button
                variant={residuoMode === 'fineAnnoCorr' ? 'default' : 'ghost'}
                size="sm"
                className="h-7 px-2.5 text-xs"
                onClick={() => setResiduoMode('fineAnnoCorr')}
                data-testid="residuo-mode-corr"
              >
                31/12/{new Date().getFullYear()}
              </Button>
            </div>
          </div>

          <div className="grid gap-6 md:grid-cols-2">
          {/* Residuo per Categoria */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <PieChartIcon className="h-4 w-4 text-amber-600" />
                Residuo Ammortizzabile per Categoria
                <span className="text-xs font-normal text-muted-foreground">
                  ({residuoMode === 'oggi' ? 'ad oggi' : residuoMode === 'fineAnnoPrec' ? `al 31/12/${new Date().getFullYear() - 1}` : `al 31/12/${new Date().getFullYear()}`})
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {dashboardData.categorieArr.map((cat, idx) => {
                  const pct = cat.valoreBene > 0 ? (cat.ammortizzato / cat.valoreBene) * 100 : 0;
                  return (
                    <div key={cat.id ?? cat.nome ?? idx} className="space-y-1.5" data-testid={`cat-residuo-${idx}`}>
                      <div className="flex items-center justify-between text-sm">
                        <span className="font-medium truncate mr-2">{cat.nome} <span className="text-xs text-muted-foreground">({cat.count})</span></span>
                        <span className="font-bold tabular-nums text-amber-600 shrink-0">{formatCurrency(cat.residuo)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-2.5 bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400 rounded-full transition-all"
                            style={{ width: `${Math.min(pct, 100)}%` }}
                          />
                        </div>
                        <span className="text-xs text-muted-foreground w-10 text-right tabular-nums">{pct.toFixed(0)}%</span>
                      </div>
                      <div className="flex justify-between text-[11px] text-muted-foreground">
                        <span>Ammortizzato: {formatCurrency(cat.ammortizzato)}</span>
                        <span>Valore: {formatCurrency(cat.valoreBene)}</span>
                      </div>
                    </div>
                  );
                })}
                {dashboardData.categorieArr.length === 0 && (
                  <p className="text-center text-muted-foreground py-4">Nessuna categoria</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Timeline Ammortamenti */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <BarChart3 className="h-4 w-4 text-indigo-600" />
                Timeline Quote Ammortamento
              </CardTitle>
              <CardDescription>Importo annuale in ammortamento</CardDescription>
            </CardHeader>
            <CardContent>
              {dashboardData.timelineData.length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={dashboardData.timelineData} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="anno" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} />
                    <ReTooltip
                      formatter={(value) => [formatCurrency(value), "Quota Annuale"]}
                      labelFormatter={(l) => `Anno ${l}`}
                      contentStyle={{ borderRadius: 8, fontSize: 12 }}
                    />
                    <Bar dataKey="quota" radius={[4, 4, 0, 0]} maxBarSize={40}>
                      {dashboardData.timelineData.map((entry) => (
                        <Cell
                          key={`cell-${entry.anno}`}
                          fill={entry.isCurrent ? '#6366f1' : '#c7d2fe'}
                          stroke={entry.isCurrent ? '#4f46e5' : 'none'}
                          strokeWidth={entry.isCurrent ? 2 : 0}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-center text-muted-foreground py-8">Nessun dato da visualizzare</p>
              )}
            </CardContent>
          </Card>
          </div>
        </div>
      )}

      {/* Info */}
      {ammortamenti.length === 0 && (
        <Card className="bg-muted/50">
          <CardContent className="pt-6">
            <p className="text-center text-muted-foreground">
              Nessun ammortamento presente. Per creare un ammortamento, vai su <strong>{t('invoices.title')}</strong> e 
              durante l&apos;allocazione seleziona l&apos;opzione &quot;Genera Ammortamento&quot;.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Table */}
      {ammortamenti.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Elenco Beni Ammortizzabili</CardTitle>
            <CardDescription>Clicca sulla freccia per espandere il piano di ammortamento</CardDescription>
          </CardHeader>
          <CardContent>
            <DataTable
              columns={columns}
              data={ammortamenti}
              onDelete={handleBulkDelete}
              idField="id"
              emptyMessage="Nessun ammortamento"
              exportFileName="piano_ammortamenti"
              renderExpandedRow={renderExpandedRow}
            />
          </CardContent>
        </Card>
      )}

      {/* Edit Dialog */}
      <Dialog open={editDialog.open} onOpenChange={(open) => setEditDialog({ open, item: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("amortization.editAmortization")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Descrizione Bene *</Label>
              <Input
                value={editForm.descrizione_bene}
                onChange={(e) => setEditForm({ ...editForm, descrizione_bene: e.target.value })}
                data-testid="input-descrizione"
              />
            </div>
            <div className="space-y-2">
              <Label>Categoria *</Label>
              <SearchableSelect 
                options={categorie.map(c => ({ value: c.id, label: `${c.nome} (${c.quota_annuale_percentuale}%)` }))}
                value={editForm.categoria_id}
                onValueChange={(v) => setEditForm({ ...editForm, categoria_id: v })}
                placeholder={t("common.selectPlaceholder")}
                searchPlaceholder="Cerca categoria..."
                testId="select-categoria"
              />
            </div>
            <div className="space-y-2">
              <Label>{t("common.notes")}</Label>
              <Textarea
                value={editForm.note}
                onChange={(e) => setEditForm({ ...editForm, note: e.target.value })}
                rows={3}
              />
            </div>
            <div className="p-3 bg-muted rounded text-sm">
              <p><strong>Valore bene:</strong> {formatCurrency(editDialog.item?.valore_bene)}</p>
              <p><strong>Data inizio:</strong> {editDialog.item?.data_inizio}</p>
              <p className="text-muted-foreground text-xs mt-1">
                Il valore del bene e la data di inizio non sono modificabili (derivano dalla fattura).
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog({ open: false, item: null })}>{t('common.cancel')}</Button>
            <Button onClick={handleEditSubmit} data-testid="save-btn">{t('common.saveChanges')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, item: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("confirm.confirmDeletion")}</DialogTitle>
            <DialogDescription>
              Eliminare l&apos;ammortamento per &quot;{deleteDialog.item?.descrizione_bene}&quot;?
              Questa azione è irreversibile.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog({ open: false, item: null })}>{t('common.cancel')}</Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-btn">{t('common.delete')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
